import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { Heading } from '../Heading/Heading';
import { AttributeRule } from '../../../types/api/AttributeGroups';
import bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';

export type AttributeRuleCombination = 'and' | 'or';

export enum AttributeRulesModifier {
  COMBOBOX = 'combobox',
}

export type AttributeRulesType = {
  rules: AttributeRule[];
  ruleCombination?: AttributeRuleCombination;
  heading?: string;
  modifier?: Modifier<AttributeRulesModifier>;
};

/**
 * Component describing the attribute rules
 */
export function AttributeRules({ rules, ruleCombination, heading, modifier }: AttributeRulesType) {
  const { t } = useTranslation('AttributeGroups');
  if (rules.length === 0) {
    return null;
  }

  return (
    <div className={bem('attribute-rules', modifier)}>
      {heading && (
        <Heading className="attribute-rules__heading font-bold-15-24" hierarchy={3}>
          {heading}
        </Heading>
      )}
      <dl
        className={
          modifier === 'combobox'
            ? 'attribute-rules__definition-list font-regular-13-19'
            : 'attribute-rules__definition-list font-regular-15-22'
        }
      >
        {rules.map((rule, index) => (
          <Fragment key={rule.attributeName + rule.condition + rule.conditionValue}>
            <dt className="invisible">{t('ruleIndex', { position: index + 1 })}</dt>
            <dd className="attribute-rules__definition-list-description">
              {`${t(`PersonAttributes:${rule.attributeName}`)} `}
              <span aria-hidden="true">{`>`}</span>
              {` (${t(`phrases:ruleSetConditions.${rule.condition}`)}) `}
              <span aria-hidden="true">{`>`}</span>
              {` ${rule.conditionValue}`}
            </dd>
            {ruleCombination && index !== rules.length - 1 && (
              <>
                <dt className="invisible">{t('ruleCombination')}</dt>
                <dd className="attribute-rules__definition-list-description">
                  <Strong>
                    {modifier === 'combobox' ? '\u00A0' : null}
                    {t(`form.connection.${ruleCombination}`)}
                    {modifier === 'combobox' ? '\u00A0' : null}
                  </Strong>
                </dd>
              </>
            )}
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
